import React, { useCallback, useEffect, useRef, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { supabase } from '../../supabaseClient';
import Modal from '../Modal';
import InfoIcon from '../InfoIcon';
import { logComplianceEvent, nextDocumentVersion } from '../../lib/complianceEvents';
import '../../lib/datepicker-theme.css';

// Compliance document upload modal. Five-state machine:
//   empty       → just opened, nothing entered yet
//   filled      → file chosen AND expiry is a valid future date
//   submitting  → POST in flight (all fields disabled, spinner on button)
//   success     → upload succeeded, 2-second auto-close timer running
//   error       → upload failed, return to filled, inline error message
//
// Opens from a DocumentRow Upload / Update button via the onUpload
// callback wired in SupplierCompliance. After success, calls onSuccess
// which the parent uses to refetch the document list via
// useSupplierCompliance – the row transitions Outstanding → Awaiting
// Review without a page refresh.
//
// File rules: PDF / JPG / PNG, 10MB max, single file, click-to-upload
// only (no drag-and-drop in v1). Expiry is a DD/MM/YYYY text input –
// native date inputs render inconsistently across browsers, and UK
// format consistency matters.
//
// Server-side re-validation happens in the upsert (file URL is built
// from the supplier's auth context). Client-side validation is the
// first gate; nothing here is trusted alone.

const MAX_FILE_BYTES = 10 * 1024 * 1024; // 10MB
const ACCEPTED_MIME = 'application/pdf,image/jpeg,image/png';
const ACCEPTED_EXTENSIONS_LABEL = 'PDF · JPG · PNG · Max 10MB';

// ============================================================================
// Date helpers
// ============================================================================

const startOfToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

// Returns { valid, daysOut, tooFar, message, tone } – drives the hint
// text below the picker. The picker itself prevents bad formats and
// the minDate prop blocks past selections; this just surfaces the
// "how far out" signal:
//   empty       → "Must be in the future" (grey)
//   < 30 days   → "In N days – this would be Expiring Soon" (amber)
//   30–60 days  → "In N days from today" (grey)
//   > 60 days   → "{year} – N months from today" (grey)
//   > 10 years  → "Are you sure? That's over 10 years away" (amber, not blocking)
const evaluateExpiryDate = (date) => {
  if (!date) return { valid: false, message: 'Must be in the future', tone: 'grey' };
  const today = startOfToday();
  const msDay = 24 * 60 * 60 * 1000;
  const daysOut = Math.round((date.getTime() - today.getTime()) / msDay);
  if (daysOut <= 0) {
    return { valid: false, message: 'Expiry must be in the future', tone: 'red' };
  }
  if (daysOut > 365 * 10) {
    return { valid: true, daysOut, tooFar: true, message: "Are you sure? That's over 10 years away", tone: 'amber' };
  }
  if (daysOut < 30) {
    return { valid: true, daysOut, message: `In ${daysOut} day${daysOut === 1 ? '' : 's'} – this would be Expiring Soon`, tone: 'amber' };
  }
  if (daysOut <= 60) {
    return { valid: true, daysOut, message: `In ${daysOut} days from today`, tone: 'grey' };
  }
  const months = Math.round(daysOut / 30);
  return { valid: true, daysOut, message: `${date.getFullYear()} – ${months} months from today`, tone: 'grey' };
};

// Date → YYYY-MM-DD for the DB (supplier_onboarding_documents.expiry_date
// is a date column, day-precision only).
const toIsoDateOnly = (d) => {
  if (!d) return null;
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

// ============================================================================
// File helpers
// ============================================================================

const validateFile = (file) => {
  if (!file) return { ok: false, message: 'No file selected' };
  const type = file.type;
  if (!ACCEPTED_MIME.split(',').includes(type)) {
    return { ok: false, message: 'PDF, JPG, or PNG only' };
  }
  if (file.size === 0) return { ok: false, message: 'File is empty' };
  if (file.size > MAX_FILE_BYTES) return { ok: false, message: 'File too large – max 10MB' };
  return { ok: true };
};

const formatFileSize = (bytes) => {
  if (!bytes) return '0 MB';
  const mb = bytes / (1024 * 1024);
  return `${mb.toFixed(1)} MB`;
};

// ============================================================================
// Inline icons (Lucide-shape, hand-coded to match the rest of the codebase)
// ============================================================================

const UploadIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#51534A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);

const FileTextIcon = ({ stroke = '#92D400' }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="9" y1="13" x2="15" y2="13" />
    <line x1="9" y1="17" x2="13" y2="17" />
  </svg>
);

const CheckIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#92D400" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const Spinner = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true" className="animate-spin">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="3" fill="none" strokeOpacity="0.25" />
    <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" fill="none" strokeLinecap="round" />
  </svg>
);

// ============================================================================
// Modal
// ============================================================================

export default function ComplianceUploadModal({ document: docMeta, supplierId, isReplacement = false, onSuccess, onClose }) {
  const [uploadState, setUploadState] = useState('empty'); // empty | filled | submitting | success | error
  const [file, setFile] = useState(null);
  // Expiry date is now a Date object (or null) – react-datepicker
  // handles all the parsing + formatting. The picker's minDate prop
  // blocks past selections natively, so the only client-side check
  // here is "is something selected".
  const [expiryDate, setExpiryDate] = useState(null);
  const [note, setNote] = useState(''); // optional note to the reviewer
  const [errorMessage, setErrorMessage] = useState(null);
  const [filePickerError, setFilePickerError] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  // Only ask for an expiry when the document is configured to ask for one.
  // This modal previously showed the field on EVERY document and blocked submit
  // until it was filled – so a document explicitly set to "admins set expiry on
  // review" still demanded a date the supplier had no way of knowing, and the
  // Submit button sat disabled with no explanation of why.
  const asksExpiry = docMeta?.supplierProvidesExpiry === true;
  const dateInfo = evaluateExpiryDate(expiryDate);
  const canSubmit = Boolean(file) && (!asksExpiry || dateInfo.valid);
  const tomorrow = (() => { const d = startOfToday(); d.setDate(d.getDate() + 1); return d; })();

  // Keep uploadState in sync with the live inputs. Submitting / success /
  // error are sticky transitions driven by the submission flow; empty
  // and filled track the live form.
  useEffect(() => {
    if (uploadState === 'submitting' || uploadState === 'success') return;
    if (uploadState === 'error') return; // stay on error until user retries
    setUploadState(canSubmit ? 'filled' : 'empty');
  }, [canSubmit, uploadState]);

  // 2-second auto-close on success. Cleared if the modal unmounts early
  // (user clicked outside / hit Esc). onSuccess fires either way – it's
  // idempotent (closes the modal + triggers a parent refetch) and the
  // parent guards against double-fire.
  useEffect(() => {
    if (uploadState !== 'success') return;
    const t = setTimeout(() => { onSuccess?.(); }, 2000);
    return () => clearTimeout(t);
  }, [uploadState, onSuccess]);

  const acceptPicked = useCallback((picked) => {
    if (!picked) return;
    const v = validateFile(picked);
    if (!v.ok) {
      setFilePickerError(v.message);
      setTimeout(() => setFilePickerError(null), 3000);
      return;
    }
    setFilePickerError(null);
    setFile(picked);
  }, []);

  const handleFilePick = useCallback((e) => {
    const picked = e.target.files?.[0];
    e.target.value = ''; // allow re-picking the same file later
    acceptPicked(picked);
  }, [acceptPicked]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    if (uploadState === 'submitting') return;
    acceptPicked(e.dataTransfer?.files?.[0]);
  }, [acceptPicked, uploadState]);

  const handleRemoveFile = useCallback(() => {
    setFile(null);
    setFilePickerError(null);
  }, []);

  const handleDateChange = useCallback((d) => {
    setExpiryDate(d);
    // If user is fixing an error after a failed submit, clear the
    // banner so they don't read a stale message.
    if (uploadState === 'error') setUploadState('filled');
  }, [uploadState]);

  const handleSubmit = useCallback(async () => {
    if (!canSubmit || !supplierId || !file) return;
    setUploadState('submitting');
    setErrorMessage(null);
    try {
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      // Unique path per upload (timestamped) so a re-upload doesn't overwrite the
      // previous version's file – each version stays downloadable from its event
      // (document-detail-page §6). The row still keeps only the latest url.
      const path = `${supplierId}/${docMeta.key}-${Date.now()}-${safeName}`;
      const { error: uploadError } = await supabase.storage
        .from('supplier-uploads')
        .upload(path, file, { contentType: file.type, upsert: false });
      if (uploadError) throw new Error(uploadError.message);

      const { data: pub } = supabase.storage.from('supplier-uploads').getPublicUrl(path);
      const fileUrl = pub?.publicUrl || null;
      const expiryIso = toIsoDateOnly(expiryDate);

      const { error: upsertError } = await supabase
        .from('supplier_onboarding_documents')
        .upsert({
          supplier_id: supplierId,
          document_key: docMeta.key,
          supplier_uploaded_file_url: fileUrl,
          supplier_uploaded_file_name: file.name,
          supplier_uploaded_at: new Date().toISOString(),
          expiry_date: expiryIso,
          expiry_approved_at: null,
          expiry_approved_by: null,
          expiry_email_sent_at: null,
          expiry_warning_email_sent_at: null,
          signed_at: null,
          signed_marked_by: null,
          // Re-upload after a rejection / request-for-info must clear the prior
          // review so the fresh file returns to Awaiting review. review_decision
          // takes precedence in computeDocumentState, so leaving 'rejected' here
          // left a re-uploaded doc stuck out of the admin queue.
          review_decision: null,
          review_reason: null,
          review_notes: null,
          reviewed_at: null,
          reviewed_by: null,
          supplier_reply: null,
          supplier_reply_at: null,
          // Optional note the supplier attached to this upload (cleared when
          // they don't add one, so it always matches the current file).
          supplier_upload_note: note.trim() || null,
          updated_at: new Date().toISOString(),
        }, { onConflict: 'supplier_id,document_key' });
      if (upsertError) throw new Error(upsertError.message);

      // Log the version onto the events spine so its file stays downloadable
      // even after a later re-upload. Best-effort – never fails the submit.
      const version = await nextDocumentVersion(supplierId, docMeta.key);
      await logComplianceEvent({
        supplierId,
        documentKey: docMeta.key,
        eventType: 'uploaded',
        actorSide: 'supplier',
        body: note.trim() || null,
        version,
        filePath: fileUrl,
        fileName: file.name,
        meta: expiryIso ? { expiry_date: expiryIso } : {},
      });

      setUploadState('success');
    } catch (err) {
      setErrorMessage(err?.message || 'Could not submit. Try again.');
      setUploadState('error');
    }
  }, [canSubmit, supplierId, file, docMeta?.key, expiryDate, note]);

  const submitting = uploadState === 'submitting';
  const success = uploadState === 'success';

  // Block close X / Esc / backdrop while a submit is in flight to avoid
  // orphan upload state. The Modal primitive will dispatch onClose for
  // backdrop + Esc – we trap it here.
  const safeClose = useCallback(() => {
    if (submitting) return;
    onClose?.();
  }, [submitting, onClose]);

  const eyebrow = isReplacement ? 'Replace Document' : 'Required Document';
  const title = isReplacement ? `Update – ${docMeta.label}` : docMeta.label;
  const submitLabel = isReplacement ? 'Submit Replacement' : 'Submit for Review';
  const successBody = isReplacement
    ? `Fusion 2K will let you know when your updated ${docMeta.label} has been approved. Until then, your previous version stays in effect.`
    : `Fusion 2K will let you know when your ${docMeta.label} has been approved.`;

  // ----------------------------------------------------------------
  // Success view – full-modal replacement during the 2-second timer
  // ----------------------------------------------------------------
  if (success) {
    return (
      <Modal open onClose={onSuccess} maxWidth={420} stripe={false} ariaLabel="Submitted for review">
        <div className="px-7 py-10 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full mb-4" style={{ backgroundColor: 'rgba(146, 212, 0, 0.1)', border: '1.5px solid rgba(146, 212, 0, 0.4)' }}>
            <CheckIcon />
          </div>
          <h2 className="font-['Metropolis'] text-lg font-extrabold text-ink mb-2">Submitted for review</h2>
          <p className="font-['Metropolis'] text-[12px] text-fusion-mid-grey leading-relaxed max-w-[320px] mx-auto mb-5">
            {successBody}
          </p>
          <p className="font-['Metropolis'] text-[10px] font-bold uppercase tracking-[0.18em] text-[#51534A]">
            Closing in 2 seconds…
          </p>
        </div>
      </Modal>
    );
  }

  // ----------------------------------------------------------------
  // Input view – empty / filled / submitting / error
  // ----------------------------------------------------------------
  return (
    <Modal open onClose={safeClose} maxWidth={420} stripe={false} ariaLabel={`Upload ${docMeta.label}`}>
      <Modal.Header
        eyebrow={eyebrow}
        title={title}
        sub={docMeta.description}
        onClose={submitting ? undefined : safeClose}
      />
      <Modal.Body>
        {/* File picker */}
        <div className="mb-5">
          <label className="block font-['Metropolis'] text-[10px] font-bold uppercase tracking-[0.2em] text-[#51534A] mb-2">
            File
          </label>
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_MIME}
            onChange={handleFilePick}
            disabled={submitting}
            style={{ display: 'none' }}
          />
          {!file ? (
            <div>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); if (!submitting) setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                disabled={submitting}
                className={`w-full bg-transparent border-2 border-dashed rounded-lg px-4 py-6 flex flex-col items-center justify-center gap-2 cursor-pointer transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed ${dragOver ? 'border-[#92D400] bg-[#92D400]/[0.05]' : 'border-line hover:border-[#DADCCD]'}`}
              >
                <UploadIcon />
                <span className="font-['Metropolis'] text-[12px] font-bold text-fusion-mid-grey">{dragOver ? 'Drop to upload' : 'Click or drag a file to upload'}</span>
                <span className="font-['Metropolis'] text-[10px] text-[#51534A]">{ACCEPTED_EXTENSIONS_LABEL}</span>
              </button>
              {filePickerError && (
                <p className="font-['Metropolis'] text-[11px] font-semibold mt-2" style={{ color: '#E0290A' }}>
                  {filePickerError}
                </p>
              )}
            </div>
          ) : (
            <div
              className="flex items-center gap-3 rounded-lg px-3 py-2.5"
              style={{ backgroundColor: 'rgba(146, 212, 0, 0.04)', border: '1px solid rgba(146, 212, 0, 0.25)', borderLeft: '3px solid #92D400' }}
            >
              <div className="w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(146, 212, 0, 0.12)' }}>
                <FileTextIcon />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-['Metropolis'] text-[12px] text-ink font-bold truncate" title={file.name}>
                  {file.name}
                </div>
                <div className="font-['Metropolis'] text-[10px] text-[#51534A]">{formatFileSize(file.size)}</div>
              </div>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={submitting}
                className="font-['Metropolis'] text-[9px] font-bold uppercase tracking-[0.15em] text-fusion-mid-grey hover:text-ink bg-transparent border-0 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Change
              </button>
              <button
                type="button"
                onClick={handleRemoveFile}
                disabled={submitting}
                aria-label="Remove file"
                className="w-6 h-6 rounded flex items-center justify-center bg-transparent border border-line text-[#51534A] hover:text-[#E0290A] hover:border-[#E0290A]/60 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
          )}
        </div>

        {/* Expiry date picker – same react-datepicker the admin uses
            on AddBrief, but date-only (no time component) because
            supplier_onboarding_documents.expiry_date is a DATE
            column. minDate=tomorrow blocks selecting today or
            earlier so the supplier can't submit an already-expired
            document.

            Shown ONLY when the document asks the supplier for it. Where the
            admin has chosen to set the expiry at review, asking here is asking
            for a date the supplier often can't know – and it was blocking
            submit until they invented one. */}
        {asksExpiry && (
        <div className="mb-1">
          <label
            className="block font-['Metropolis'] text-[10px] font-bold uppercase tracking-[0.2em] text-[#51534A] mb-2"
          >
            Expiry date
            <InfoIcon heading="Why we ask for an expiry date" ariaLabel="Why do you ask for an expiry date?">For documents that expire (like insurance), the expiry date lets Fusion 2K move you to 'Expiring Soon' in good time, so you can renew before it lapses.</InfoIcon>
          </label>
          <DatePicker
            selected={expiryDate}
            onChange={handleDateChange}
            minDate={tomorrow}
            disabled={submitting}
            dateFormat="dd/MM/yyyy"
            placeholderText="dd/mm/yyyy"
            isClearable
            className="w-full bg-white border border-line rounded-md px-3 py-2.5 font-['Metropolis'] text-[14px] text-ink placeholder:text-[#51534A] focus:outline-none focus:border-[#92D400] disabled:opacity-50"
            wrapperClassName="block w-full" />
          <p
            className="font-['Metropolis'] text-[11px] mt-1.5 leading-snug"
            style={{ color: dateInfo.tone === 'red' ? '#E0290A' : dateInfo.tone === 'amber' ? '#E89A2E' : '#51534A' }}
          >
            {dateInfo.message}
          </p>
        </div>
        )}

        {/* Optional note to the reviewer */}
        <div className="mt-5">
          <label htmlFor="compliance-upload-note" className="block font-['Metropolis'] text-[10px] font-bold uppercase tracking-[0.2em] text-[#51534A] mb-2">
            Note for Fusion 2K <span className="text-[#9BA08C] normal-case tracking-normal font-semibold">· optional</span>
          </label>
          <textarea
            id="compliance-upload-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            disabled={submitting}
            rows={2}
            placeholder="Anything Fusion 2K should know about this document?"
            className="w-full bg-white border border-line rounded-md px-3 py-2.5 font-['Metropolis'] text-[13px] text-ink placeholder:text-[#51534A] focus:outline-none focus:border-[#92D400] disabled:opacity-50 resize-y" />
        </div>

        {/* Error banner */}
        {uploadState === 'error' && errorMessage && (
          <div
            role="alert"
            className="mt-4 rounded-md px-3 py-2.5 font-['Metropolis'] text-[12px]"
            style={{ backgroundColor: 'rgba(224, 41, 10, 0.08)', border: '1px solid rgba(224, 41, 10, 0.35)', color: '#E0290A' }}
          >
            Couldn't submit. {errorMessage}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <div className="flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={safeClose}
            disabled={submitting}
            className="font-['Metropolis'] text-[11px] font-bold uppercase tracking-[0.15em] text-[#51534A] hover:text-fusion-mid-grey bg-transparent border-0 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit || submitting}
            className="font-['Metropolis'] text-[11px] font-bold uppercase tracking-[0.15em] text-[#131313] bg-[#92D400] border border-[#92D400] rounded-full px-5 py-2 inline-flex items-center gap-2 transition-[filter,opacity] duration-150 hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:brightness-100"
          >
            {submitting ? (
              <>
                <Spinner />
                <span>Submitting…</span>
              </>
            ) : (
              <span>{submitLabel}</span>
            )}
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}
